<template>
  <div class="admin-page">
    <div class="admin-header">
      <div>
        <router-link to="/pointe/admin/applications" class="back-link">← Applications</router-link>
        <h1>Import Historical Application</h1>
      </div>
    </div>

    <p class="page-desc">Use this form to add a retroactive application record for work done before the app existed.</p>

    <form @submit.prevent="save" class="import-form">
      <div class="form-row">
        <div class="form-group">
          <label>Dancer Name *</label>
          <input v-model="form.dancerName" type="text" required class="form-input" />
        </div>
        <div class="form-group">
          <label>Dancer Email *</label>
          <input v-model="form.dancerEmail" type="email" required class="form-input" />
        </div>
      </div>

      <div class="form-row">
        <div class="form-group">
          <label>Cohort *</label>
          <select v-model="form.cohortId" required class="form-input" @change="onCohortChange">
            <option value="">Select cohort…</option>
            <option v-for="c in cohorts" :key="c.id" :value="c.id">{{ c.name }}</option>
          </select>
        </div>
        <div class="form-group">
          <label>Overall Result *</label>
          <select v-model="form.overallResult" required class="form-input">
            <option value="">Select…</option>
            <option value="approved">Approved</option>
            <option value="needs_work">Needs Work</option>
          </select>
        </div>
      </div>

      <div class="form-group">
        <label>PDF Link (Google Drive or Dropbox URL)</label>
        <input v-model="form.pdfUrl" type="url" class="form-input" placeholder="https://drive.google.com/…" />
      </div>

      <div class="form-group">
        <label>Admin Notes</label>
        <textarea v-model="form.adminNotes" class="form-input form-textarea" rows="3"></textarea>
      </div>

      <!-- Criteria checklist -->
      <div v-if="criteria.length" class="criteria-section">
        <h2>Criteria Results</h2>
        <div v-for="criterion in criteria" :key="criterion.id" class="criterion-item">
          <div class="criterion-header">
            <button
              type="button"
              class="toggle-btn"
              :class="{ pass: criteriaResults[criterion.id] === true, fail: criteriaResults[criterion.id] === false }"
              @click="cycleCriterion(criterion.id)"
            >
              <span v-if="criteriaResults[criterion.id] === true">✓ Pass</span>
              <span v-else-if="criteriaResults[criterion.id] === false">✗ Needs Work</span>
              <span v-else>— Not Reviewed</span>
            </button>
            <span class="criterion-name">{{ criterion.title }}</span>
          </div>
          <textarea
            v-model="criteriaNotes[criterion.id]"
            class="notes-input"
            rows="1"
            :placeholder="'Notes…'"
          ></textarea>
        </div>
      </div>

      <!-- Link to consultation -->
      <div class="form-group">
        <label>Link to Foot 360 Consultation (optional)</label>
        <input v-model="consultationSearch" type="text" class="form-input" placeholder="Search by dancer email…" @input="searchConsultations" />
        <div v-if="consultationResults.length" class="consult-results">
          <div
            v-for="c in consultationResults"
            :key="c.id"
            class="consult-result-row"
            :class="{ selected: form.linkedConsultationId === c.id }"
            @click="toggleConsultationLink(c)"
          >
            {{ c.dancerEmail }} — {{ formatDate(c.consultationDate) }}
            <span v-if="form.linkedConsultationId === c.id"> ✓</span>
          </div>
        </div>
        <p v-if="form.linkedConsultationId" class="info-note">Consultation linked.</p>
      </div>

      <p v-if="error" class="error-text">{{ error }}</p>

      <div class="form-actions">
        <router-link to="/pointe/admin/applications" class="btn-secondary">Cancel</router-link>
        <button type="submit" class="btn-primary" :disabled="saving">
          {{ saving ? 'Saving…' : 'Import Application' }}
        </button>
      </div>
    </form>
  </div>
</template>

<script>
import { db } from '@/firebase'

export default {
  data () {
    return {
      cohorts: [],
      criteria: [],
      criteriaResults: {},
      criteriaNotes: {},
      consultationSearch: '',
      consultationResults: [],
      saving: false,
      error: '',
      form: {
        dancerName: '',
        dancerEmail: '',
        cohortId: '',
        cohortName: '',
        overallResult: '',
        pdfUrl: '',
        adminNotes: '',
        linkedConsultationId: null
      }
    }
  },
  async created () {
    const [cohortsSnap, criteriaSnap] = await Promise.all([
      db.collection('pointe-cohorts').orderBy('createdAt', 'desc').get(),
      db.collection('pointe-criteria').where('active', '==', true).orderBy('order').get()
    ])
    this.cohorts = cohortsSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }))
    this.criteria = criteriaSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }))
    this.criteria.forEach(c => {
      this.$set(this.criteriaResults, c.id, null)
      this.$set(this.criteriaNotes, c.id, '')
    })
  },
  methods: {
    onCohortChange () {
      const cohort = this.cohorts.find(c => c.id === this.form.cohortId)
      this.form.cohortName = cohort ? cohort.name : ''
    },
    cycleCriterion (id) {
      const current = this.criteriaResults[id]
      if (current === undefined || current === null) {
        this.$set(this.criteriaResults, id, true)
      } else if (current === true) {
        this.$set(this.criteriaResults, id, false)
      } else {
        this.$set(this.criteriaResults, id, null)
      }
    },
    async searchConsultations () {
      if (!this.consultationSearch || this.consultationSearch.length < 3) {
        this.consultationResults = []
        return
      }
      const snap = await db.collection('pointe-consultations')
        .where('dancerEmail', '==', this.consultationSearch.toLowerCase())
        .limit(5).get()
      this.consultationResults = snap.docs.map(doc => ({ id: doc.id, ...doc.data() }))
    },
    toggleConsultationLink (c) {
      this.form.linkedConsultationId = this.form.linkedConsultationId === c.id ? null : c.id
    },
    formatDate (ts) {
      if (!ts) return '—'
      const d = ts.toDate ? ts.toDate() : new Date(ts)
      return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
    },
    buildCriteriaObj () {
      const obj = {}
      this.criteria.forEach(c => {
        obj[c.id] = {
          passed: this.criteriaResults[c.id] !== undefined ? this.criteriaResults[c.id] : null,
          adminNotes: this.criteriaNotes[c.id] || ''
        }
      })
      return obj
    },
    async save () {
      this.saving = true
      this.error = ''
      try {
        const data = {
          dancerName: this.form.dancerName.trim(),
          dancerEmail: this.form.dancerEmail.toLowerCase().trim(),
          cohortId: this.form.cohortId,
          cohortName: this.form.cohortName,
          overallResult: this.form.overallResult,
          pdfUrl: this.form.pdfUrl.trim(),
          adminNotes: this.form.adminNotes.trim(),
          linkedConsultationId: this.form.linkedConsultationId || null,
          criteria: this.buildCriteriaObj(),
          isHistorical: true,
          status: this.form.overallResult === 'approved' ? 'approved' : 'needs_work',
          paymentStatus: 'paid',
          videoConfirmed: true,
          createdAt: new Date(),
          reviewedAt: new Date()
        }
        await db.collection('pointe-applications').add(data)
        this.$router.push('/pointe/admin/applications')
      } catch (e) {
        this.error = e.message
      }
      this.saving = false
    }
  }
}
</script>

<style scoped>
.admin-page {
  max-width: 800px;
  margin: 0 auto;
  padding: 32px 24px;
  font-family: 'Fanwood Text', Georgia, serif;
}

.admin-header {
  display: flex;
  align-items: flex-start;
  margin-bottom: 8px;
}

.admin-header h1 {
  font-family: 'Quattrocento', Georgia, serif;
  font-size: 26px;
  color: #1a1a1a;
  margin: 4px 0 0;
}

.back-link { font-size: 13px; color: #888; text-decoration: none; display: block; margin-bottom: 4px; }
.back-link:hover { color: #1a1a1a; }

.page-desc { font-size: 15px; color: #666; margin: 0 0 28px; }

.import-form { display: flex; flex-direction: column; gap: 0; }

.form-row { display: flex; gap: 16px; }
.form-row .form-group { flex: 1; }

.form-group { margin-bottom: 20px; }

.form-group label {
  display: block;
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: .06em;
  color: #1a1a1a;
  margin-bottom: 6px;
}

.form-input {
  width: 100%;
  border: 1px solid #e8e5e1;
  padding: 10px 12px;
  font-family: 'Fanwood Text', Georgia, serif;
  font-size: 15px;
  border-radius: 2px;
  box-sizing: border-box;
  color: #1a1a1a;
  background: #fff;
}

.form-input:focus { outline: none; border-color: #1a1a1a; }
.form-textarea { resize: vertical; }

.criteria-section { margin-bottom: 24px; }

.criteria-section h2 {
  font-family: 'Quattrocento', Georgia, serif;
  font-size: 18px;
  color: #1a1a1a;
  margin: 0 0 12px;
}

.criterion-item {
  border: 1px solid #e8e5e1;
  border-radius: 4px;
  padding: 12px 14px;
  margin-bottom: 8px;
  background: #fff;
}

.criterion-header { display: flex; align-items: center; gap: 12px; margin-bottom: 6px; }

.criterion-name { font-size: 15px; font-weight: 600; color: #1a1a1a; }

.toggle-btn {
  border: none;
  padding: 5px 12px;
  border-radius: 20px;
  cursor: pointer;
  font-size: 12px;
  font-family: 'Fanwood Text', Georgia, serif;
  background: #f0f0f0;
  color: #888;
  white-space: nowrap;
}

.toggle-btn.pass { background: #e8f5e9; color: #2e7d32; }
.toggle-btn.fail { background: #fce4ec; color: #c62828; }

.notes-input {
  width: 100%;
  border: 1px solid #e8e5e1;
  padding: 6px 10px;
  font-family: 'Fanwood Text', Georgia, serif;
  font-size: 13px;
  border-radius: 2px;
  box-sizing: border-box;
  resize: vertical;
  background: #faf9f7;
  color: #1a1a1a;
}

.notes-input:focus { outline: none; border-color: #1a1a1a; background: #fff; }

.consult-results { border: 1px solid #e8e5e1; border-radius: 2px; margin-top: 4px; background: #fff; }

.consult-result-row {
  padding: 8px 12px;
  font-size: 13px;
  cursor: pointer;
  border-bottom: 1px solid #f0eeec;
}

.consult-result-row:last-child { border-bottom: none; }
.consult-result-row:hover { background: #f9f8f6; }
.consult-result-row.selected { background: #e8f5e9; }

.info-note { font-size: 13px; color: #888; margin: 6px 0 0; font-style: italic; }

.form-actions { display: flex; gap: 12px; justify-content: flex-end; margin-top: 8px; }

.btn-primary {
  background: #1a1a1a;
  color: #fff;
  border: none;
  padding: 12px 24px;
  font-family: 'Fanwood Text', Georgia, serif;
  font-size: 15px;
  cursor: pointer;
  border-radius: 2px;
}

.btn-primary:disabled { opacity: 0.5; cursor: default; }

.btn-secondary {
  background: #fff;
  color: #1a1a1a;
  border: 1px solid #1a1a1a;
  padding: 12px 24px;
  font-family: 'Fanwood Text', Georgia, serif;
  font-size: 15px;
  text-decoration: none;
  display: inline-block;
  border-radius: 2px;
}

.error-text { color: #c00; font-size: 14px; }
</style>
